import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from '@angular/fire/storage';
import { Observable } from 'rxjs';
import { Cuenta } from '../models/cuenta.model';

@Injectable({ providedIn: 'root' })
export class CuentaService {
  private firestore = inject(Firestore);
  private storage   = inject(Storage);

  private col(residencialId: string) {
    return collection(this.firestore, `residenciales/${residencialId}/cuentas`);
  }

  getAll(residencialId: string): Observable<Cuenta[]> {
    return collectionData(this.col(residencialId), { idField: 'id' }) as Observable<Cuenta[]>;
  }

  // Lectura puntual — sin listener. Usada una vez por sesión para cargar catálogos en páginas de residente.
  async getAllOnce(residencialId: string): Promise<Cuenta[]> {
    const snap = await getDocs(query(this.col(residencialId), where('activo', '==', true)));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Cuenta));
  }

  getById(residencialId: string, cuentaId: string): Observable<Cuenta> {
    const r = doc(this.firestore, `residenciales/${residencialId}/cuentas/${cuentaId}`);
    return docData(r, { idField: 'id' }) as Observable<Cuenta>;
  }

  async crear(residencialId: string, datos: Omit<Cuenta, 'id'>): Promise<string> {
    const docRef = await addDoc(this.col(residencialId), datos);
    return docRef.id;
  }

  async actualizar(residencialId: string, cuentaId: string, cambios: Partial<Omit<Cuenta, 'id'>>): Promise<void> {
    const r = doc(this.firestore, `residenciales/${residencialId}/cuentas/${cuentaId}`);
    await updateDoc(r, { ...cambios });
  }

  async desactivar(residencialId: string, cuentaId: string): Promise<void> {
    const r = doc(this.firestore, `residenciales/${residencialId}/cuentas/${cuentaId}`);
    await updateDoc(r, { activo: false });
  }

  async uploadIcono(residencialId: string, cuentaId: string, file: File): Promise<string> {
    const ext     = file.name.split('.').pop() ?? 'png';
    const iconRef = ref(this.storage, `residenciales/${residencialId}/cuentas/${cuentaId}/icono.${ext}`);
    await uploadBytes(iconRef, file);
    return getDownloadURL(iconRef);
  }

  async deleteIcono(url: string): Promise<void> {
    try {
      await deleteObject(ref(this.storage, url));
    } catch { /* ignorar si ya fue eliminado */ }
  }
}
